import React from "react";
import { number, string, bool } from "prop-types";
import { useTranslation } from "@lib/i18n";

import Typography from "./Typography";
import HorizontalLoader from "./HorizontalLoader";

import { getFormattedPrice } from "@shared/v2/lib/getFormattedPrice";
import { getIsCashJourneySelected } from "@shared/v2/lib/getIsCashJourneySelected";

import { useCurrencySettingsSelector } from "@redux/reducers/appConfig";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import { twMerge } from "tailwind-merge";

const PriceFormatter = ({
  amount,
  className,
  currencySymbol,
  firstPaymentAmount,
  isVisible = true,
  journey,
  monthlyTotalPrice,
  priceFormat,
  showOnlyCash,
}) => {
  const { t } = useTranslation();
  const currencySettings = useCurrencySettingsSelector();

  const targetFormat = priceFormat ?? currencySettings.format;
  const targetCurrency = currencySymbol ?? currencySettings.symbol;

  const isCashJourneySelected = getIsCashJourneySelected(journey);

  const isB2BSelected = useIsB2BSelected();

  const downPayment = monthlyTotalPrice
    ? getFormattedPrice(monthlyTotalPrice, targetFormat, targetCurrency)
    : null;

  return (
    isVisible && (
      <div className={twMerge("text-black text-base", className)}>
        {showOnlyCash || isCashJourneySelected ? (
          <Typography className="inline uppercase font-semibold">
            {getFormattedPrice(amount, targetFormat, targetCurrency)}
          </Typography>
        ) : (
          <div data-id="price-switcher-monthly-price">
            {monthlyTotalPrice ? (
              <Typography className="inline text-lg uppercase font-semibold">
                {getFormattedPrice(
                  monthlyTotalPrice,
                  targetFormat,
                  targetCurrency
                )}
                <sup className="pl-1 text-xxs uppercase">
                  {isB2BSelected ? t("price.b2b.month") : t("price.b2c.month")}
                </sup>
              </Typography>
            ) : (
              <HorizontalLoader />
            )}
            <a
              className={twMerge(
                "text-white",
                firstPaymentAmount ? "block" : "hidden"
              )}
              data-id="price-switcher-price-comment"
              href="#"
            >
              {t("price.afterFirstPayment", { downPayment })}
              <div>{t("price.personalizeFinancing")}</div>
            </a>
          </div>
        )}
      </div>
    )
  );
};

PriceFormatter.propTypes = {
  amount: number,
  className: string,
  currencySymbol: string,
  firstPaymentAmount: number,
  isVisible: bool,
  journey: string,
  monthlyTotalPrice: number,
  priceFormat: string,
  showOnlyCash: bool,
};

export default PriceFormatter;
